import { FLAG_ON, StorageKey } from '../../lib/storageKeys';
import { readStorage } from '../../lib/safeStorage';
import { detectRuntimeEnv, RuntimeEnv } from '../../lib/runtimeEnv';

/** Hash paths of the print sheets; they never sit behind the welcome gate. */
const PRINT_PATH_PREFIX = '/print/';

export function isPrintPath(pathname: string): boolean {
  return pathname.startsWith(PRINT_PATH_PREFIX);
}

/** The test-only bypass: both flags must be set, so a production user cannot hit it by one stray key. */
export function isGateBypassed(): boolean {
  return readStorage(StorageKey.TestHooks) === FLAG_ON && readStorage(StorageKey.GateBypass) === FLAG_ON;
}

/**
 * Pages that open without an account and never show the welcome page: the landing page of the link in a
 * verification mail (it is opened in whatever browser the mail was read in, logged in or not) and the static
 * legal pages (linked from the registration form).
 */
const UNGATED_PATH_PREFIXES = ['/verify-email', '/legal/'] as const;

export function isUngatedPath(pathname: string): boolean {
  return UNGATED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/** Whether the welcome gate applies at all on this page (browser, not a print sheet or an open page, no bypass). */
export function gateApplies(pathname: string): boolean {
  return (
    detectRuntimeEnv() === RuntimeEnv.Browser &&
    !isPrintPath(pathname) &&
    !isUngatedPath(pathname) &&
    !isGateBypassed()
  );
}

/** How the user got in through the welcome page. */
export const EntryKind = {
  /** A new account or a new passphrase space: nothing to wait for, the first-run screen follows. */
  New: 'new',
  /** An existing account or space: wait for the first sync, which may bring the user's settings. */
  Existing: 'existing',
} as const;
export type EntryKind = (typeof EntryKind)[keyof typeof EntryKind];
